import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { useContext, useEffect, useState } from "react";
import { APIContext } from "../../Contexts/APIContext";
import CalendarDay from "./CalendarDay";
import CalendarViewModel from "../Organisation/ViewModels/CalendarViewModel";
import CalendarExportsDialog from "./CalendarExportsDialog";

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const addDays = (date: Date, days: number) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const startOfWeek = (date: Date) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const offset = (result.getDay() + 6) % 7;
    result.setDate(result.getDate() - offset);
    return result;
};

const formatDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatMonthLabel = (start: Date, end: Date) => {
    const startLabel = start.toLocaleDateString('default', { month: 'long' });
    const endLabel = end.toLocaleDateString('default', { month: 'long' });

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return start.toLocaleDateString('default', { month: 'long', year: 'numeric' });
    }

    if (start.getFullYear() === end.getFullYear()) {
        return `${startLabel} - ${endLabel}`;
    }

    return `${startLabel} ${start.getFullYear()} - ${endLabel} ${end.getFullYear()}`;
};

export default function Calendar() {
    const { get, loading } = useContext(APIContext);
    const [calendar, setCalendar] = useState<CalendarViewModel>(new CalendarViewModel());
    const [viewAnchorDate, setViewAnchorDate] = useState<Date>(() => new Date());
    const [open, setOpen] = useState<boolean>(false);
    const today = new Date();
    const visibleStartDate = startOfWeek(viewAnchorDate);
    const visibleEndDate = addDays(visibleStartDate, 41);
    const visibleStartKey = formatDateKey(visibleStartDate);
    const visibleEndKey = formatDateKey(visibleEndDate);

    useEffect(() => {
        get("Organisation", `GetCalendar?startDate=${visibleStartKey}&endDate=${visibleEndKey}`, onSucess);
    }, [visibleStartKey, visibleEndKey]);

    const onSucess = (data: CalendarViewModel) => {
        setCalendar(data);
    };

    const shiftWeeks = (weekIncrement: number) => {
        const newDate = new Date(viewAnchorDate);
        newDate.setDate(newDate.getDate() + (weekIncrement * 7));
        setViewAnchorDate(newDate);
    };

    const shiftMonths = (monthIncrement: number) => {
        const newDate = new Date(viewAnchorDate);
        newDate.setMonth(newDate.getMonth() + monthIncrement);
        setViewAnchorDate(newDate);
    };

    const resetToToday = () => {
        setViewAnchorDate(new Date());
    };

    const visibleDates = Array.from({ length: 42 }).map((_, index) => addDays(visibleStartDate, index));

    const title = formatMonthLabel(visibleStartDate, visibleEndDate);

    const getEventsForDate = (date: Date) => {
        const dateKey = formatDateKey(date);
        return calendar.events.filter(event => formatDateKey(new Date(event.date)) === dateKey);
    };

    const isDateToday = (date: Date) => formatDateKey(date) === formatDateKey(today);

    const isDateWeekend = (date: Date) => date.getDay() === 0 || date.getDay() === 6;

    return (
        <Grid container>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} size={12}>
                <Grid>
                    <Typography variant="h6" noWrap sx={{ minWidth: 200 }}>
                        {title}
                    </Typography>
                </Grid>
                <Grid>
                    <Button size="small" onClick={() => setOpen(true)} fullWidth={false}>
                        Exports
                    </Button>
                </Grid>
            </Grid>
            <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }} size={12}>
                {daysOfWeek.map((day, index) => { return <Grid key={day + index} size={{ xs: 12 / 7 }} sx={{ border: '2px solid #ccc' }}><Typography>{day}</Typography></Grid> })}
                {visibleDates.map((date) => {
                    const events = getEventsForDate(date);
                    const isToday = isDateToday(date);
                    const isWeekend = isDateWeekend(date);
                    return <CalendarDay key={formatDateKey(date)} events={events} dayOfMonthIndex={date.getDate()} isToday={isToday} isWeekend={isWeekend} />;
                })}
            </Grid>
            <Grid container justifyContent="center" alignItems="center" sx={{ mt: 2 }} size={12}>
                <Grid>
                    <Button size="small" variant="contained" disabled={loading} onClick={() => shiftMonths(-1)} fullWidth={false}>
                        -1 Month
                    </Button>
                </Grid>
                <Grid>
                    <Button size="small" variant="contained" disabled={loading} onClick={() => shiftWeeks(-1)} fullWidth={false}>
                        -1 Week
                    </Button>
                </Grid>
                <Grid>
                    <Button size="small" variant="contained" disabled={loading} onClick={resetToToday} fullWidth={false}>
                        Today
                    </Button>
                </Grid>
                <Grid>
                    <Button size="small" variant="contained" disabled={loading} onClick={() => shiftWeeks(1)} fullWidth={false}>
                        +1 Week
                    </Button>
                </Grid>
                <Grid>
                    <Button size="small" variant="contained" disabled={loading} onClick={() => shiftMonths(1)} fullWidth={false}>
                        +1 Month
                    </Button>
                </Grid>
            </Grid>
            <CalendarExportsDialog open={open} onClose={() => setOpen(false)} />
        </Grid >
    );
};
